import type { QueryClient } from '@tanstack/react-query';
import {
  persistQueryClientRestore,
  persistQueryClientSave,
  persistQueryClientSubscribe,
} from '@tanstack/react-query-persist-client';
import { createPersister, maxCacheAgeMs } from '../api/persist';
import { rememberSession } from '../api/hooks';

export const cacheBuster = `qronstat-${import.meta.env.VITE_BUILD_ID ?? 'dev'}`;
const lastUserKey = 'qs.lastUser';

function readLastUser(): string | null {
  try {
    return localStorage.getItem(lastUserKey);
  } catch {
    return null;
  }
}

function writeLastUser(userId: string | null): void {
  try {
    if (userId) localStorage.setItem(lastUserKey, userId);
    else localStorage.removeItem(lastUserKey);
  } catch {
    return;
  }
}

const dehydrateOptions = {
  shouldDehydrateQuery: (q: { meta?: Record<string, unknown> | undefined; state: { status: string } }) =>
    q.meta?.persist === true && q.state.status === 'success',
};

export class DeviceCache {
  private unsubscribe: (() => void) | null = null;
  private userId: string | null = null;
  readonly restored: Promise<void>;

  constructor(private readonly client: QueryClient) {
    const last = readLastUser();
    this.userId = last;
    this.restored = last
      ? persistQueryClientRestore({
          queryClient: client,
          persister: createPersister(last),
          maxAge: maxCacheAgeMs,
          buster: cacheBuster,
        }).catch(() => undefined)
      : Promise.resolve();
  }

  async attach(userId: string): Promise<void> {
    await this.restored;
    if (this.userId && this.userId !== userId) {
      this.unsubscribe?.();
      await createPersister(this.userId).removeClient();
      this.client.removeQueries({ predicate: (q) => q.queryKey[0] !== 'session' });
    }
    if (this.unsubscribe && this.userId === userId) return;
    this.userId = userId;
    writeLastUser(userId);
    const persister = createPersister(userId);
    this.unsubscribe = persistQueryClientSubscribe({
      queryClient: this.client,
      persister,
      buster: cacheBuster,
      dehydrateOptions,
    });
    await persistQueryClientSave({
      queryClient: this.client,
      persister,
      buster: cacheBuster,
      dehydrateOptions,
    }).catch(() => undefined);
  }

  async clear(): Promise<void> {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.userId) await createPersister(this.userId).removeClient();
    this.userId = null;
    writeLastUser(null);
    rememberSession(null);
    this.client.clear();
  }
}
